import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Download, ArrowUpRight } from "lucide-react";
import ProductCard from "@/components/ProductCard";
import { PRODUCTS } from "@/data/catalogue";

const APPLICATION_FILTERS = ["All", "Food & Beverage", "Cosmetics", "Nutraceutical", "Pharma / Diagnostics", "Sports / Supplements"];
const APP_MATCH = {
  "Food & Beverage": ["Food coloring", "Beverages", "Confectionery", "Ice cream", "Dairy"],
  "Cosmetics": ["Skincare serums"],
  "Nutraceutical": ["Supplements", "Nutraceuticals", "Functional beverages", "Functional foods", "Clinical nutrition", "Advanced bioactive formulations"],
  "Pharma / Diagnostics": ["Pharma R&D", "Diagnostics", "Fluorescence-linked systems", "High-spec pharma workflows", "Institutional R&D", "Research workflows"],
  "Sports / Supplements": ["Sports nutrition", "Dietary supplements"],
};

const GRADE_FILTERS = ["All", "E18", "E25", "E30", "E40", "Organic"];

const Products = () => {
  const [appFilter, setAppFilter] = useState("All");
  const [gradeFilter, setGradeFilter] = useState("All");

  const filtered = useMemo(() => {
    return PRODUCTS.filter((p) => {
      const gradeOk = gradeFilter === "All" || p.grade === gradeFilter;
      const appOk = appFilter === "All" || p.applications.some((a) => APP_MATCH[appFilter]?.includes(a));
      return gradeOk && appOk;
    });
  }, [appFilter, gradeFilter]);

  return (
    <div data-testid="page-products" className="pt-[72px]">
      {/* HEADER */}
      <section className="bg-brand-black text-brand-cream relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-25" />
        <div className="container-wide py-20 md:py-32 relative">
          <p className="eyebrow-light mb-5">◦  Grade Architecture · July 2027 Rollout</p>
          <h1 className="font-display text-4xl sm:text-5xl md:text-7xl tracking-tight leading-[1.05] max-w-4xl">
            Phycocyanin grades engineered <span className="italic text-brand-gold">for launch-stage planning.</span>
          </h1>
          <p className="mt-5 md:mt-6 text-brand-cream/75 max-w-2xl leading-relaxed text-sm md:text-base">
            Four phycocyanin grades plus certified organic spirulina, each positioned around application fit, documentation pathway, and technical review — not immediate ordering. Built for buyers planning July 2027 supply.
          </p>
        </div>
      </section>

      {/* FILTERS */}
      <section className="bg-brand-cream border-b border-brand-green/15 sticky top-[72px] z-30 backdrop-blur-md">
        <div className="container-wide py-4 md:py-5 flex flex-col gap-3 md:gap-4 lg:flex-row lg:gap-8 lg:items-center">
          <div className="flex items-start gap-3 flex-wrap">
            <span className="eyebrow whitespace-nowrap pt-1.5">Grade</span>
            <div className="flex flex-wrap gap-1.5">
              {GRADE_FILTERS.map((g) => (
                <button
                  key={g}
                  data-testid={`filter-grade-${g}`}
                  onClick={() => setGradeFilter(g)}
                  className={`px-3 py-2 text-[11px] font-mono uppercase tracking-widest border transition min-h-[36px] ${gradeFilter === g ? "bg-brand-green text-brand-cream border-brand-green" : "border-brand-green/25 text-brand-black/70 hover:border-brand-gold hover:text-brand-gold"}`}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-start gap-3 flex-wrap">
            <span className="eyebrow whitespace-nowrap pt-1.5">Application</span>
            <div className="flex flex-wrap gap-1.5">
              {APPLICATION_FILTERS.map((a) => (
                <button
                  key={a}
                  data-testid={`filter-app-${a.replace(/[\s/]+/g, '-')}`}
                  onClick={() => setAppFilter(a)}
                  className={`px-3 py-2 text-[11px] font-mono uppercase tracking-widest border transition min-h-[36px] ${appFilter === a ? "bg-brand-teal text-brand-cream border-brand-teal" : "border-brand-green/25 text-brand-black/70 hover:border-brand-gold hover:text-brand-gold"}`}
                >
                  {a}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* GRID */}
      <section className="container-wide py-14 md:py-20">
        {filtered.length === 0 ? (
          <p className="text-center text-brand-black/60 py-20" data-testid="empty-products">No grades match the selected filters.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 auto-rows-fr">
            {filtered.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
      </section>

      {/* RESOURCES TEASER STRIP */}
      <section className="bg-brand-cream border-t border-brand-green/15" data-testid="products-resources-teaser">
        <div className="container-wide py-14 md:py-20">
          <div className="flex items-end justify-between gap-6 mb-8 md:mb-10 flex-wrap">
            <div>
              <p className="eyebrow mb-3">◦  Take the spec sheets with you</p>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl text-brand-black leading-tight tracking-tight max-w-2xl">
                Download grade-by-grade <span className="italic text-brand-teal">technical brochures.</span>
              </h2>
            </div>
            <Link to="/resources" data-testid="products-cta-resources" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-brand-teal hover:text-brand-gold transition">
              All resources <ArrowUpRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {["e18", "e25", "e30", "e40"].map((g) => (
              <Link
                key={g}
                to="/resources"
                data-testid={`products-teaser-brochure-${g}`}
                className="bg-white border border-brand-green/15 p-5 hover:border-brand-gold transition group flex items-center justify-between gap-3"
              >
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-brand-black/50">Brochure</p>
                  <p className="font-display text-lg text-brand-black mt-1">Phycocyanin {g.toUpperCase()}</p>
                </div>
                <Download size={16} className="text-brand-black/40 group-hover:text-brand-gold transition" />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;
